function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useRef, useCallback, useEffect, useState, FC, Ref, ChangeEventHandler, CSSProperties, InputHTMLAttributes, RefObject, ElementType, useMemo } from 'react';
import cn from 'classnames';
import { AddonAfter } from '../../_internal_/components/AddonAfter';
import { Popup, bindSupportingElement, usePopupState, UsePopupStateTriggers } from '../Popup';
import { useForkRef } from '../../hooks/useForkRef';
import { TextInput } from '../TextInput';
import { useUniqId } from '../../hooks/useUniqId';
import { Calendar, ICalendarProps, isDateBGreaterThanDateA, isTwoDatesEqual, parseInputStringToRangeDates, parseInputStringToSingleDate, ICalendarDate, transformRangeDatesToInputString, transformSingleDateToInputString } from '../Calendar';
import { isDateExists } from '../Calendar/helpers/dateFormatters';
require("./DatePicker.css");
var styles = {
  "Root": "DatePicker-module-Root_1LV",
  "TextInput": "DatePicker-module-TextInput_1RL",
  "hasHiddenIndicator": "DatePicker-module-hasHiddenIndicator_2tX",
  "TextInputBox": "DatePicker-module-TextInputBox_15R",
  "focused": "DatePicker-module-focused_3B5",
  "focusedIcon": "DatePicker-module-focusedIcon_1CQ",
  "Popup": "DatePicker-module-Popup_1T8",
  "errorState": "DatePicker-module-errorState_2na"
};
export const cnDatePicker = cn('DatePicker');
export const defaultDatePickerErrors = {
  incorrectFormatMsg: 'Введенный формат не поддерживается.',
  rangeDatesEqualMsg: 'Даты диапазона должны отличаться',
  rangeDatesWrongOrderMsg: 'Неправильный порядок дат диапазона.',
  incompleteInput: 'Введите диапазон дат.',
  unavailableDate: 'Данное значение недоступно для выбора.'
};
export const DatePicker = ({
  calendarType = 'single',
  className,
  controlRef,
  dateFormat = 'YYYY.MM.DD',
  errorMessages = defaultDatePickerErrors,
  innerRef,
  includeDayNavigation,
  includeResultPreview,
  includeTailsFromOtherMonths,
  label,
  name,
  hasClear = false,
  hasExpandIndicator = true,
  hasIndependentLabel = false,
  onBlur,
  onChange,
  onCheck,
  onClickDate,
  onCollapse,
  onExpand,
  onFocus,
  onKeyDown,
  style = {},
  separator = '.',
  rangeDivider = ' - ',
  placeholder,
  tag = 'span',
  selectYearsFrom,
  selectYearsTill,
  selectDatesFrom,
  selectDatesTill,
  selectedDateOne,
  selectedDateTwo,
  disabledDates,
  ...rest
}) => {
  const Component = tag;
  const [inputError, setInputError] = useState(undefined);
  const popupState = usePopupState({
    triggers: [UsePopupStateTriggers.focus, UsePopupStateTriggers.click]
  });
  const [isFocused, setIsFocused] = useState(false);
  const popupId = useRef(`popup-${Math.random().toString(16).slice(-4)}`);
  const popupRef = useRef();
  const id = useUniqId('date-picker');
  const inputId = useUniqId('input');
  const _controlRef = useRef();
  const _innerRef = useRef();
  const ownRef = useForkRef(_controlRef, controlRef);
  const [dateOne, setDateOne] = useState(selectedDateOne);
  const [dateTwo, setDateTwo] = useState(calendarType === 'double' ? selectedDateTwo : undefined);
  const defaultInputValue = useMemo(() => {
    let result = '';
    if (calendarType === 'double' && selectedDateOne && selectedDateTwo && isDateExists(selectedDateOne) && isDateExists(selectedDateTwo) && isDateBGreaterThanDateA(selectedDateOne, selectedDateTwo)) {
      result = transformRangeDatesToInputString({
        dateOne: selectedDateOne,
        dateTwo: selectedDateTwo,
        dateFormat,
        separator,
        rangeDivider
      });
    } else if (calendarType === 'single' && selectedDateOne && isDateExists(selectedDateOne)) {
      result = transformSingleDateToInputString({
        date: selectedDateOne,
        dateFormat,
        separator
      });
    }
    return result;
  }, [selectedDateOne, selectedDateTwo, calendarType, dateFormat, separator, rangeDivider]);
  const [inputValue, setInputValue] = useState(defaultInputValue);
  const singleAutoPlaceholder = dateFormat.split('.').join(separator);
  const rangeAutoPlaceholder = `${singleAutoPlaceholder}${rangeDivider}${singleAutoPlaceholder}`;
  const autoPlaceholder = placeholder || (calendarType === 'double' ? rangeAutoPlaceholder : singleAutoPlaceholder);
  useEffect(() => {
    const handleCollapse = () => {
      onCollapse?.(popupId.current);
    };
    const handleExpand = e => {
      if (e.target?.getAttribute('data-popup-value')?.includes(popupId.current)) {
        onExpand?.(popupId.current);
      }
    };
    if (typeof window === 'undefined') {
      return;
    }
    document?.addEventListener('eapteka-ui:popupclose', handleCollapse);
    document?.addEventListener('eapteka-ui:popupopen', handleExpand);
    return () => {
      document?.removeEventListener('eapteka-ui:popupclose', handleCollapse);
      document?.removeEventListener('eapteka-ui:popupopen', handleExpand);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popupState.visibleId]);
  const handleParseSingleValue = useCallback(() => {
    const result = parseInputStringToSingleDate({
      inputString: inputValue,
      separator,
      dateFormat
    });
    !result && Boolean(inputValue.length) && setInputError(errorMessages.incorrectFormatMsg);
    if (result) {
      if (selectDatesFrom && isDateBGreaterThanDateA(result, selectDatesFrom) || selectDatesTill && isDateBGreaterThanDateA(selectDatesTill, result)) {
        setInputError(errorMessages.unavailableDate);
      }
      setDateOne(result ? result : undefined);
    }
  }, [inputValue, errorMessages, separator, dateFormat, selectDatesFrom, selectDatesTill]);
  const handleParseRangeValue = useCallback(() => {
    const result = parseInputStringToRangeDates({
      inputString: inputValue,
      separator,
      rangeDivider,
      dateFormat
    });
    !result && inputValue.length && setInputError(errorMessages.incorrectFormatMsg);
    let resultDateOne = result?.[0];
    let resultDateTwo = result?.[1];
    if (result && (!result?.[0] || !result?.[1])) {
      setInputError(errorMessages.incompleteInput);
    }
    if (result?.[0] && result?.[1]) {
      if (selectDatesFrom && isDateBGreaterThanDateA(result[0], selectDatesFrom) || selectDatesTill && isDateBGreaterThanDateA(selectDatesTill, result[1])) {
        setInputError(errorMessages.unavailableDate);
      } else {
        const isInputDatesEqual = isTwoDatesEqual(result[0], result[1]);
        isInputDatesEqual && setInputError(errorMessages.rangeDatesEqualMsg);
        const isWrongOrder = isDateBGreaterThanDateA(result[1], result[0]);
        isWrongOrder && setInputError(errorMessages.rangeDatesWrongOrderMsg);
        resultDateOne = result[0];
        resultDateTwo = !isInputDatesEqual && !isWrongOrder ? result[1] : undefined;
      }
    }
    setDateOne(resultDateOne);
    setDateTwo(resultDateTwo);
  }, [inputValue, errorMessages, separator, rangeDivider, dateFormat, selectDatesFrom, selectDatesTill]);
  const handleFocus = useCallback(e => {
    onFocus && onFocus(e);
    setInputError(undefined);
    popupState.onOpen(e);
    setIsFocused(true);
  }, [onFocus, popupState]);
  const handleBlur = useCallback(e => {
    onBlur && onBlur(e);
    calendarType === 'single' && handleParseSingleValue();
    calendarType === 'double' && handleParseRangeValue();
    !inputError && onCheck?.(dateOne, dateTwo);
    popupState.onClose(e);
    setIsFocused(false);
  }, [dateOne, dateTwo, inputError, onBlur, calendarType, handleParseSingleValue, handleParseRangeValue, onCheck, popupState]);
  const handleKeyDown = useCallback(e => {
    onKeyDown && onKeyDown(e);
    if (e.key === 'Enter') {
      calendarType === 'single' && handleParseSingleValue();
      calendarType === 'double' && handleParseRangeValue();
      !inputError && onCheck?.(dateOne, dateTwo);
      popupState.onClose(e);
      setIsFocused(false);
    }
  }, [dateOne, dateTwo, inputError, onKeyDown, calendarType, handleParseSingleValue, handleParseRangeValue, onCheck, popupState]);
  const handleChange = useCallback(e => {
    onChange?.(e);
    setInputError(undefined);
    if (calendarType === 'single') {
      const result = parseInputStringToSingleDate({
        inputString: e.target.value,
        separator,
        dateFormat
      });
      setDateOne(result || undefined);
    }
    if (calendarType === 'double') {
      const result = parseInputStringToRangeDates({
        inputString: e.target.value,
        separator,
        rangeDivider,
        dateFormat
      });
      let resultDateOne = result?.[0];
      let resultDateTwo = result?.[1];
      if (result?.[0] && result?.[1]) {
        const isInputDatesEqual = isTwoDatesEqual(result[0], result[1]);
        const isWrongOrder = isDateBGreaterThanDateA(result[1], result[0]);
        resultDateOne = result[0];
        resultDateTwo = !isInputDatesEqual && !isWrongOrder ? result[1] : undefined;
      }
      setDateOne(resultDateOne);
      setDateTwo(resultDateTwo);
    }
    setInputValue(e.target.value);
  }, [onChange, calendarType, rangeDivider, dateFormat, separator]);
  const handleCalendarCheck = useCallback((dateOne, dateTwo) => {
    if (calendarType === 'single') {
      setDateOne(dateOne);
      setInputValue(dateOne ? transformSingleDateToInputString({
        date: dateOne,
        dateFormat,
        separator
      }) : '');
    }
    if (calendarType === 'double') {
      setDateOne(dateOne);
      setDateTwo(dateTwo);
      setInputValue(transformRangeDatesToInputString({
        dateOne,
        dateTwo,
        dateFormat,
        separator,
        rangeDivider
      }));
    }
  }, [calendarType, dateFormat, rangeDivider, separator]);
  const handleMouseDown = useCallback(e => {
    e.preventDefault();
  }, []);
  const popupProps = {
    ...{
      ...bindSupportingElement(popupId.current, popupState),
      onFocus: handleFocus
    }
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Component, {
    style: style,
    className: cn(styles.Root, styles[`${inputError && 'error'}State`], className, {
      [styles.hasIndependentLabel]: hasIndependentLabel
    }),
    ref: innerRef
  }, /*#__PURE__*/React.createElement(TextInput, _extends({}, popupProps, rest, {
    addonAfter: hasExpandIndicator ? /*#__PURE__*/React.createElement(AddonAfter, _extends({}, popupProps, {
      className: cn({
        [styles.focusedIcon]: isFocused
      }),
      isExpanded: isFocused,
      inputRef: _controlRef,
      expandIconName: "time",
      collapseIconName: "time"
    })) : null,
    "aria-controls": id,
    "aria-expanded": isFocused,
    className: cn(styles.TextInput, {
      [styles.hasHiddenIndicator]: !hasExpandIndicator
    }),
    classNameBox: cn(styles.TextInputBox, {
      [styles.expanded]: isFocused,
      [styles.focused]: isFocused
    }),
    hasClear: hasClear,
    innerRef: _innerRef,
    controlRef: ownRef,
    id: inputId,
    name: name,
    onKeyDown: handleKeyDown,
    onBlur: handleBlur,
    onChange: handleChange,
    role: "combobox",
    label: label,
    value: inputValue,
    hasIndependentLabel: hasIndependentLabel,
    variant: "default",
    state: inputError ? 'error' : undefined,
    hint: inputError,
    placeholder: autoPlaceholder
  }))), /*#__PURE__*/React.createElement(Popup, {
    className: cn(styles.Popup, {
      [styles.focused]: isFocused
    }),
    id: popupId.current,
    offset: 4,
    innerRef: popupRef,
    isVisible: isFocused,
    placement: "bottom-start",
    target: _innerRef.current,
    variant: "default"
  }, /*#__PURE__*/React.createElement("div", {
    onMouseDown: handleMouseDown
  }, /*#__PURE__*/React.createElement(Calendar, {
    calendarType: calendarType,
    dateFormat: dateFormat,
    includeDayNavigation: includeDayNavigation,
    includeResultPreview: includeResultPreview,
    includeTailsFromOtherMonths: includeTailsFromOtherMonths,
    onCheck: handleCalendarCheck,
    onClickDate: onClickDate,
    selectedDateOne: dateOne,
    selectedDateTwo: dateTwo,
    selectDatesFrom: selectDatesFrom,
    selectDatesTill: selectDatesTill,
    selectYearsFrom: selectYearsFrom,
    selectYearsTill: selectYearsTill,
    disabledDates: disabledDates
  }))));
};
if (process.env.NODE_ENV !== 'production') {
  DatePicker.displayName = 'DatePicker';
}